import React, { useEffect } from 'react';
import { APITypes } from '../../../../primary-record-types';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import { LoadableAvatar, getFallbackFromName } from '../../../v2BaseComponents/Avatar';
import urls from '../../../../utils/urls';
import { Select } from '../../../v2BaseComponents/Inputs';
import { SelectOption } from '../../../v2BaseComponents/Inputs/Select/types';
import { getIntl } from '../../../../utils/intl';
import Text from '../../../v2BaseComponents/Text';
import getName from '../../../../utils/getName';

const intl = getIntl('edit-client-profile', {
    groups: 'Groups',
});

export type EditClientGroupsOwnProps = {
    clientProfile: APITypes.organizationService.GetOrganizationMedicalProfileResponse | null;
    clientFirstName: string;
    clientLastName: string;
    clientRecordGroups: APITypes.organizationService.ListOrganizationClientRecordOrganizationGroupsResponse['items'];
    organizationGroups: APITypes.organizationService.ListOrganizationGroupsResponse['items'];
    selectedGroupIds: string[];
    setSelectedGroupIds: (groupIds: string[]) => void;
};

export type EditClientGroupsProps = EditClientGroupsOwnProps;

export type UseSelectedGroupIdsArgs = {
    clientRecordGroups: APITypes.organizationService.ListOrganizationMedicalProfileOrganizationGroupsResponse['items'];
    organizationGroups: APITypes.organizationService.ListOrganizationGroupsResponse['items'];
};

export type UseSelectedGroupIdsReturnValue = [string[], (groupIds: string[]) => void];

export const useSelectedGroupIds = ({
    clientRecordGroups,
    organizationGroups,
}: UseSelectedGroupIdsArgs): UseSelectedGroupIdsReturnValue => {
    const [selectedGroupIds, setSelectedGroupIds] = React.useState<string[]>([]);
    // initialize group selection when data loaded
    useEffect(() => {
        if (!clientRecordGroups) return;

        const newSelectedGroupIds = clientRecordGroups
            .map((group) => group.organizationGroupId)
            .filter((groupId) => organizationGroups.some((group) => group.organizationGroupId === groupId));

        setSelectedGroupIds(newSelectedGroupIds);
    }, [clientRecordGroups, organizationGroups]);

    return [selectedGroupIds, setSelectedGroupIds];
};

const EditClientGroups: React.FC<EditClientGroupsProps> = (props) => {
    return (
        <Stack vertical fullWidth align="center" space={contentStackSpacing}>
            {props.clientProfile ? (
                <Stack vertical fullWidth align="center" space={1}>
                    <LoadableAvatar
                        avatarUrl={urls.api.medicalProfiles.avatar(props.clientProfile.medicalProfileId)}
                        size="xl"
                        fallbackName={getFallbackFromName(props.clientFirstName, props.clientLastName)}
                    />
                    <Text variant="body-1">
                        {getName({ firstName: props.clientFirstName, lastName: props.clientLastName })}
                    </Text>
                </Stack>
            ) : null}
            <Select
                multiple={true}
                selectOptions={props.organizationGroups
                    .map<SelectOption>((group) => ({
                        label: group.metadata.name,
                        value: group.organizationGroupId,
                        testID: group.metadata.name,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                value={(props.selectedGroupIds || []) as any}
                onChange={(groupIds) => props.setSelectedGroupIds(groupIds || [])}
                title={intl('groups')}
                testID="edit-client-profile-groups-select"
            />
        </Stack>
    );
};

export default React.memo(EditClientGroups);
